import ChevronImage from "@assets/video_widget/Chevron.webp";
import ArticleCard from "@blocks/Card/ArticleCard";
import MITTBanner from "@blocks/EventBanner/MITTBanner";
import PromoteEpaper from "@blocks/PromoteEpaper/PromoteEpaper";
import TranslateBanner from "@components/Layouts/Home/TranslateBanner";
import { ForexSection } from "@container/SideBlock/ForexSection";
import { GoldSection } from "@container/SideBlock/GoldSection";
import MostRead from "@container/SideBlock/MostRead";
import Taboola from "@elements/Advertisement/Taboola";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import Divider from "@elements/Divider/Divider";
import Heading from "@elements/Typography/Heading";
import { isArrayWithElements } from "@helper/utils";
import useSwrApi from "@hooks/useSwrApi";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { BudgetBook } from "@routes/Keyword/Variants/BudgetVariant";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { ArticleAdPageTargetingType, PageAdTargetValue } from "@typings/Ads.d";
import { SideBlockArticleResponse } from "@typings/APIResponse";
import { ForexResponse, GoldResponse } from "@typings/HomePagePayloadType";
import { SidebarSectionsArticlesPayload } from "@typings/PagePayloadType";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

export type SideBlockProps = {
  className?: string;
  sectionName?: string;
  pageAdTargetValue: PageAdTargetValue;
  promoteEpaper?: boolean;
  promoteMMEpaper?: boolean;
  goldPrices?: GoldResponse;
  forexPairs?: ForexResponse;
  hasTaboola?: boolean;
  hasTranslate?: boolean;
  hideAdForMobile?: boolean;
  articlePageTarget?: ArticleAdPageTargetingType[];
  gsChannels?: string[];
  epaperCoverImageUrl?: string;
  mostRead?: ProcessedArticleData[];
  sidebarSections?: SidebarSectionsArticlesPayload;
  promoteMITTBanner?: boolean;
  enableEditorialSection?: boolean;
  budget2026PdfLink?: string;
};

export default function SideBlock({
  className,
  sectionName,
  hasTaboola,
  goldPrices,
  forexPairs,
  pageAdTargetValue,
  promoteEpaper,
  promoteMMEpaper,
  hasTranslate,
  hideAdForMobile = false,
  articlePageTarget,
  gsChannels,
  epaperCoverImageUrl,
  mostRead,
  sidebarSections,
  promoteMITTBanner = false,
  enableEditorialSection = true,
  budget2026PdfLink,
}: SideBlockProps) {
  let { EditorialList, SocialList }: SidebarSectionsArticlesPayload = {};
  if (!sidebarSections) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useSwrApi<SideBlockArticleResponse>("side-block-articles");
    EditorialList = data?.EditorialList;
    SocialList = data?.SocialList;
  } else {
    EditorialList = sidebarSections.EditorialList;
    SocialList = sidebarSections.SocialList;
  }
  const { isScreenLG } = useWindowSize();

  const SectionArticleConfig = {
    elements: { hasParagraph: false, hasDate: true },
    overrideStyle: {
      cardWrapper: "flex-row flex-row-reverse",
      titleStyle:
        "font-semibold font-primary text-2sm text-gray-100 hover:text-gray-100",
      imageWrapperStyle:
        "max-w-[175px] lg:w-[175px] lg:h-[90px] lg:max-h-[90px]",
      contentWrapper: "gap-xs",
      titleWrapper: "gap-0",
      imageStyle: "h-[90px]",
    },
  };

  return (
    <div className={twMerge("lg:w-4/12 lg:pr-xxs lg:pl-md min-h-[800px]")}>
      <div data-testid="side-block" className={twMerge("w-full", className)}>
        {/* Advertisement */}
        {(isScreenLG || !hideAdForMobile) && (
          <>
            <AdsImu2
              sectionName={sectionName}
              pageAdTargetType={pageAdTargetValue}
              articlePageTarget={articlePageTarget}
              gsChannels={gsChannels}
            />
            <Divider className="border-b border-gray-2200" />
          </>
        )}
        {budget2026PdfLink && (
          <BudgetBook
            pdfLink={budget2026PdfLink}
            tagName={"பட்ஜெட் 2026"}
            className="hidden lg:block"
          />
        )}
        {/* Promote epaper block */}
        {promoteEpaper && (
          <PromoteEpaper epaperCoverImageUrl={epaperCoverImageUrl} />
        )}
        {promoteMMEpaper && (
          <PromoteEpaper
            epaperCoverImageUrl={epaperCoverImageUrl}
            publication="mm"
          />
        )}

        {promoteMITTBanner && (
          <>
            <MITTBanner />
            <Divider className="my-5 border-b border-gray-2200" />
          </>
        )}

        {/* Taboola Ads */}
        {hasTaboola && (
          <Taboola
            pageType="homepage"
            mode="thumbnails-base-widget"
            container="right-rail-thumbnails"
            placement="Right Rail Thumbnails"
            targetType="mix"
            isFlush={true}
          />
        )}
        {goldPrices && (
          <>
            <GoldSection goldPrices={goldPrices} />
            <Divider className="mt-5 border-b border-gray-2200" />
          </>
        )}
        {forexPairs && (
          <>
            <ForexSection forexPairs={forexPairs} />
            <Divider className="mt-5 border-b border-gray-2200" />
          </>
        )}
        {isArrayWithElements(SocialList) ? (
          <div className="w-full border-b border-gray-2200">
            <Heading
              Element="h2"
              className="text-red-100 text-md font-bold hover:underline hover:cursor-pointer hover:text-red-200 mt-5"
            >
              <Link reloadDocument to={"/community"} target="_blank">
                சமூகம்
                <img
                  alt="toggle-button-chevie"
                  height={26}
                  width={26}
                  className="pl-100 inline"
                  src={ChevronImage}
                />
              </Link>
            </Heading>
            {SocialList.map((section, i, row) => (
              <ArticleCard
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                key={section.id}
                displayWidth={175}
                displayHeight={90}
                {...SectionArticleConfig}
                {...section}
                overrideStyle={{
                  cardWrapper:
                    i + 1 === row.length
                      ? "w-full lg:w-full flex-row-reverse pb-5 border-0"
                      : "w-full lg:w-full flex-row-reverse pb-5",
                  titleStyle:
                    "font-semibold font-primary text-sm2 text-gray-100 hover:text-gray-100",
                  imageWrapperStyle: "w-40 max-w-44 lg:w-40 h-23 max-sm:w-31.5",
                  imageStyle: "w-full h-23",
                }}
                refMedium={"community_section"}
              />
            ))}
          </div>
        ) : null}
        {/* <Divider className="mt-5 border-b border-gray-2200" /> */}
        <MostRead mostRead={mostRead} />
        {enableEditorialSection && isArrayWithElements(EditorialList) ? (
          <div className="w-full border-b border-gray-2200">
            <Heading
              Element="h2"
              className="text-red-100 text-md font-bold hover:underline hover:cursor-pointer hover:text-red-200 mt-5"
            >
              <Link reloadDocument to={"/editorial"} target="_blank">
                தலையங்கம்
                <img
                  alt="toggle-button-chevie"
                  height={26}
                  width={26}
                  className="pl-100 inline"
                  src={ChevronImage}
                />
              </Link>
            </Heading>
            {EditorialList.map((section, i, row) => (
              <ArticleCard
                key={section.id}
                displayWidth={175}
                displayHeight={90}
                {...SectionArticleConfig}
                {...section}
                overrideStyle={{
                  cardWrapper:
                    i + 1 === row.length
                      ? "w-full lg:w-full flex-row-reverse pb-5 border-0"
                      : "w-full lg:w-full flex-row-reverse pb-5",
                  titleStyle:
                    "font-semibold font-primary text-sm2 text-gray-100 hover:text-gray-100",
                  imageWrapperStyle: "w-40 max-w-44 lg:w-40 h-23 max-sm:w-31.5",
                  imageStyle: "w-full h-23",
                }}
                refMedium={"editorial_section"}
              />
            ))}
          </div>
        ) : null}
        {hasTranslate && (
          <TranslateBanner mobileView={!isScreenLG} className="mt-5" />
        )}
      </div>
    </div>
  );
}
